import React, { useEffect } from "react";

import Topbar from "../../components/topbar";
import AddCountryModal from "../../components/popup/add-country";

import type { Column } from '../../components/admin-table';
import { Box } from "@mui/material";
import AddBtn from "../../components/ui/add-btn";
import DynamicAdminTable from "../../components/admin-table";
import Pagination from "../../components/pagination";
import DeletePopup from "../../components/popup/delete";

import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";
import { useToast } from "../../providers/toaster-provider";

const PAGE_SIZE = 10;

const Country: React.FC = () => {
    const { showSuccess, showError } = useToast();
    const [data, setData] = React.useState([]);
    const [openModal, setOpenModal] = React.useState<string>('');
    const [selectedRow, setSelectedRow] = React.useState<{
        id: number;
        country: string;
        timezone: string;
        currency: string;
        dateFormat: string;
        fiscalYearStartMonth?: number | null;
        status: boolean
    } | null>(null);
    const [page, setPage] = React.useState(1);

    const [fetch, setFetch] = useApi();
    const [create, setCreate] = useApi();
    const [update, setUpdate] = useApi();
    const [remove, setRemove] = useApi();

    useEffect(() => {
        document.title = "FineAnts Admin | Country";
    }, []);

    useEffect(() => {
        const { url, method } = buildApiRequestConfig(routes.country.fetch, [{ param: 'showDisabled', value: true }]);
        setFetch({ url, method });
    }, [create, update, remove]);

    useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (data) {
            setData(data.map((item: any) => ({
                id: item.id,
                country: item.country,
                timezone: item.timezone,
                currency: item.currency,
                dateFormat: item.dateformat,
                fiscalYearStartMonth: item.fiscalYearStartMonth,
                createdAt: item.createdAt,
                status: item.isEnabled,
            })));
        }
        if (error) {
            showError("Failed to fetch countries");
        }
    }, [fetch]);

    React.useEffect(() => {
        const { data, isLoading, error } = create;
        if (data && !isLoading) {
            showSuccess("Country created successfully");
        }
        if (error) {
            showError("Failed to create country");
        }
    }, [create]);

    React.useEffect(() => {
        const { data, isLoading, error } = update;
        if (data && !isLoading) {
            showSuccess("Country updated successfully");
        }
        if (error) {
            showError("Failed to update country");
        }
    }, [update]);

    React.useEffect(() => {
        const { data, isLoading, error } = remove;
        if (data && !isLoading) {
            showSuccess("Country deleted successfully");
        }
        if (error) {
            showError("Failed to delete country");
        }
    }, [remove]);

    const handleSubmit = (newCountry: { country: string; timezone: string; currency: string; dateFormat: string; fiscalYearStartMonth: number }) => {
        if (openModal === 'edit' && selectedRow) {
            const { url, method } = buildApiRequestConfig(routes.country.update, [{ param: 'id', value: selectedRow.id }]);
            setUpdate({ url, method, body: newCountry });
        } else {
            const { url, method } = buildApiRequestConfig(routes.country.create);
            setCreate({ url, method, body: newCountry });
        }
        setOpenModal('');
    }

    const columns: Column[] = [
        { id: 'country', label: ' Country', size: 100, onSort: (direction) => console.log(`Sort by createdAt ${direction}`) },
        { id: 'timezone', label: ' Timezone', size: 150, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'currency', label: ' Currency', size: 150, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'createdAt', label: 'Date Created', size: 150, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { 
            id: 'status', 
            label: 'Status', 
            type: 'toggle', 
            size: 150, 
            onToggle: (row, val) => { 
                const { url, method } = buildApiRequestConfig(routes.country.update, [{ param: 'id', value: row.id }]);
                setUpdate({ url, method, body: { isEnabled: val } });
            } 
        },
        { 
            id: 'actions', 
            label: 'Actions', 
            size: 50, 
            type: 'actions', 
            onDelete: (row) => {
                setSelectedRow({
                    id: row.id,
                    country: row.country,
                    timezone: row.timezone,
                    currency: row.currency,
                    dateFormat: row.dateFormat,
                    fiscalYearStartMonth: row.fiscalYearStartMonth,
                    status: row.isEnabled
                });
                setOpenModal('delete');
            },
            onEdit: (row) => {
                setSelectedRow({
                    id: row.id,
                    country: row.country,
                    timezone: row.timezone,
                    currency: row.currency,
                    dateFormat: row.dateFormat,
                    fiscalYearStartMonth: row.fiscalYearStartMonth,
                    status: row.isEnabled
                });
                setOpenModal('edit');
            }
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <AddCountryModal
                open={openModal === 'add' || openModal === 'edit'}
                onClose={() =>{ setOpenModal(''); setSelectedRow(null); }}
                onSubmit={handleSubmit}
                isEdit={openModal === 'edit'}
                editData={selectedRow || undefined}
            />
            <DeletePopup
                open={openModal === 'delete'}
                message="Delete Country?"
                description="This action will permanently remove the selected country."
                onCancel={() => setOpenModal('')}
                image={{ src: '', width: 0, height: 0 }}
                onConfirm={() => {
                    const { url, method } = buildApiRequestConfig(routes.country.delete, [{ param: 'id', value: selectedRow?.id }]);
                    setRemove({ url, method });
                    setOpenModal('');
                }}
            />
            <Topbar title="Country"/>
            <div style={{ padding: '10px', background: 'var(--color-page-background)', height: 'calc(100vh - 86px)' }}>
                <Box mb={1} display="flex" justifyContent="flex-end">
                    <AddBtn onClick={() => setOpenModal('add')} label="Add Country" />
                </Box>
                <DynamicAdminTable 
                    columns={columns} 
                    data={data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)} 
                    pagination={
                        <Pagination
                            currentPage={page}
                            totalPages={Math.ceil(data.length / PAGE_SIZE)}
                            onPageChange={setPage}
                            pageSize={PAGE_SIZE}
                            totalItems={data.length}
                        />
                    }
                />
            </div>
        </div>
    );
};

export default Country;